import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CommonDatagrid } from '../../components/DataGrid';
import { ViewButton } from '../../components/Buttons/ViewButton';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import { PackPickService } from '../../api/services/packPickService';
import { IPackedOrderHistoryEntry } from '../../types/api/response/packpickresponse';
import { PackedOrderDetailDialog } from './PackedOrderDetailDialog';

const STATUS_COLOR: Record<
  string,
  'default' | 'warning' | 'info' | 'success' | 'error' | 'primary'
> = {
  START: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
  FAILED_ORDER_NOT_FOUND: 'error',
  LOCUS_UPDATE_FAILED: 'error',
  CIN7_FULFILLMENT_FAILED: 'error',
  CIN7_INVOICING_FAILED: 'error',
};

const formatDate = (val?: string | null) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

export const PackedOrdersPage = () => {
  const [orders, setOrders] = useState<IPackedOrderHistoryEntry[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IPackedOrderHistoryEntry | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const load = async (p = page, s = pageSize, q = search) => {
    setLoading(true);
    try {
      const res = await PackPickService.getPackedOrders(p, s, q || undefined);
      setOrders(res.content ?? []);
      setTotalElements(res.page?.totalElements ?? 0);
    } catch {
      // error handled silently — could dispatch notification if needed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };
  const handleSearch = (q: string) => {
    setSearch(q);
    setPage(0);
  };

  const onClickView = (order: IPackedOrderHistoryEntry) => {
    setSelectedOrder(order);
    setIsOpenDialog(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'order_no',
        headerName: 'Order No',
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.6,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.row.status}
            color={STATUS_COLOR[params.row.status] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'station_name',
        headerName: 'Station',
        flex: 0.6,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{params.row.station_name ?? '—'}</Typography>
        ),
      },
      {
        field: 'received_at',
        headerName: 'Recorded At',
        flex: 0.8,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{formatDate(params.row.received_at)}</Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ViewButton onClick={() => onClickView(params.row as IPackedOrderHistoryEntry)} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Packed Orders
      </Typography>

      <CommonDatagrid
        data={orders}
        columns={columns}
        rowId="id"
        serverPagination
        page={page}
        pageSize={pageSize}
        rowCount={totalElements}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onChangeSearchKeyword={handleSearch}
        searchbarPlaceholder="Search by order number..."
        headerActionButtons={<RefreshButton onClick={() => load(page, pageSize, search)} />}
      />

      {selectedOrder && (
        <PackedOrderDetailDialog
          orderNumber={selectedOrder.order_no}
          isOpenDialog={isOpenDialog}
          setIsOpenDialog={setIsOpenDialog}
        />
      )}
    </Box>
  );
};
